(() => {
  if (process.env.NODE_ENV === 'production') console.log = () => {};
})();

import * as Sentry from '@sentry/remix';
import { withSentry } from '@sentry/remix';
import { json, type LinksFunction, type LoaderFunctionArgs } from '@remix-run/node';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
  isRouteErrorResponse,
} from '@remix-run/react';

import stylesheet from '~/tailwind.css?url';
import { TopProgressBar } from '~/components/TopProgressBar';
import FeaturebaseMessenger from '~/components/FeaturebaseMessenger';
import { getAuthenticatedUser } from '~/utils/supabase.server';

export function headers() {
  return {
    'X-Frame-Options': 'SAMEORIGIN',
    'Content-Security-Policy': "frame-ancestors 'self'",
  };
}

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: stylesheet },
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Noto+Color+Emoji&family=Roboto:ital,wght@0,400;0,700;1,400;1,700&family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&display=swap',
  },
  // Featurebase SDK - preload for faster widget loading
  { rel: 'preconnect', href: 'https://do.featurebase.app' },
  { rel: 'dns-prefetch', href: 'https://do.featurebase.app' },
  {
    rel: 'preload',
    href: 'https://do.featurebase.app/js/sdk.js',
    as: 'script',
  },
];

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await getAuthenticatedUser(request);
  return json({ user });
}

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" data-theme="light" className="scroll-smooth">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        {/* Featurebase SDK - load early in head for fastest widget appearance */}
        <script id="featurebase-sdk" src="https://do.featurebase.app/js/sdk.js" async />
      </head>
      <body className="min-h-screen bg-background text-foreground">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function App() {
  const { user } = useLoaderData<typeof loader>();

  return (
    <>
      <TopProgressBar />
      {/* <div
        role="banner"
        className="fixed top-0 left-0 right-0 z-[100] h-11 flex items-center justify-center bg-amber-500/95 text-black px-4 text-sm font-medium"
      >
        Supabase (our database provider) is currently down, so we are too. Thank
        you for your patience while they fix it.
      </div>
      <div className="h-11 shrink-0" aria-hidden /> */}
      <Outlet />
      <FeaturebaseMessenger email={user?.email} userId={user?.id} />
    </>
  );
}

export default withSentry(App);

export function ErrorBoundary() {
  const error = useRouteError();
  Sentry.captureRemixErrorBoundaryError(error);

  let title = 'Something went wrong';
  let message = 'An unexpected error occurred. Please try again or go back to the dashboard.';

  if (isRouteErrorResponse(error)) {
    title = `${error.status} - ${error.statusText || 'Error'}`;
    if (error.status === 404) {
      message = "The page you're looking for doesn't exist.";
    } else if (error.status === 413) {
      message =
        'The uploaded file is too large for this request. Please upload a smaller image or fewer images and try again.';
    } else if (error.status === 500) {
      message = 'A server error occurred. Please try again in a moment.';
    } else {
      message = error.data?.message || message;
    }
  } else if (error instanceof Error) {
    // Surface actionable messages (e.g. expired token) to the user
    if (error.message.includes('session has expired') || error.message.includes('reconnect your')) {
      message = error.message;
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="text-5xl">⚠️</div>
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        <p className="text-gray-600">{message}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="/dashboard"
            className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Dashboard
          </a>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  );
}